import { ComponentStore } from '@ngrx/component-store';
import { inject, Injectable } from '@angular/core';
import { catchError, concatMap } from 'rxjs/operators';
import { ICorrelation } from '@app/api/interfaces/correlation.interface';
import { EMPTY, Observable, of } from 'rxjs';
import equal from 'fast-deep-equal';
import { IGcComponentStore } from '@app/core/interfaces/gc-component-store.interface';
import { FLOATING_POPUP_DATA } from '@app/main/main-content/floating-popup-manager/providers/floating-popup-data.token';
import { Store } from '@ngrx/store';
import { selectMainProjectId } from '@app/main/main-screen/store/main-selection/main-shared-selection/main-shared-selection.selectors';
import { CorrelationsChartDataApiService } from '@app/api/services/correlation-chart-data-api.service';
import { FloatingPopupService } from '@app/main/main-content/floating-popup-manager/services/floating-popup.service';
import {
  IFpCorrelationSpectrumState,
  initialFpCorrelationSpectrumState,
} from '@app/main/leak-detection/floating-popups/floating-popup-correlation/tabs/fp-correlation-spectrum/services/fp-correlation-spectrum/fp-correlation-spectrum.state';
import {
  FpCorrelationSpectrumActionGetData,
  FpCorrelationSpectrumActionGetDataError,
  FpCorrelationSpectrumActionGetDataSuccesses,
  FpCorrelationSpectrumActions,
  FpCorrelationSpectrumActionToggleChartFullscreen,
  FpCorrelationSpectrumActionType,
} from '@app/main/leak-detection/floating-popups/floating-popup-correlation/tabs/fp-correlation-spectrum/services/fp-correlation-spectrum/fp-correlation-spectrum.actions';
import { convertChartDataInArray } from '@app/shared/utils/base-chart-data';
import {
  ICorrelationSpectrumChartCommonData,
  ICorrelationSpectrumChartDataPoint,
} from '@app/api/interfaces/chart-data/correlation-spectrum';

@Injectable()
export class FpCorrelationSpectrumStore
  extends ComponentStore<IFpCorrelationSpectrumState>
  implements IGcComponentStore<FpCorrelationSpectrumActions>
{
  public readonly data = inject<ICorrelation>(FLOATING_POPUP_DATA);
  protected readonly store = inject(Store);
  protected readonly correlationsChartDataApiService = inject(CorrelationsChartDataApiService);
  public readonly popupService: FloatingPopupService = inject(FloatingPopupService);

  constructor() {
    super(initialFpCorrelationSpectrumState);
  }

  public readonly date$ = this.select(s => s.date, { equal });
  public readonly popupMainData$ = this.select(s => s.popupMainData, { equal });
  public readonly data$ = this.select(s => s.data, { equal });
  public readonly error$ = this.select(s => s.error, { equal });

  private mainProjectId = this.store.selectSignal(selectMainProjectId);

  public readonly loading$ = this.select(s => s.loading, { equal });

  public readonly isChartFullscreenActive$ = this.select(s => s.isChartFullscreenActive, { equal });

  public dispatch = this.effect((action$: Observable<FpCorrelationSpectrumActions>) =>
    action$.pipe(
      concatMap(action => {
        switch (action.type) {
          case FpCorrelationSpectrumActionType.GetData: {
            this.reducerGetData();
            this.effectGetData(action);
            break;
          }
          case FpCorrelationSpectrumActionType.GetDataSuccesses: {
            this.reducerGetDataSuccesses(action);
            break;
          }
          case FpCorrelationSpectrumActionType.GetDataError: {
            this.reducerGetDataError(action);
            break;
          }
          case FpCorrelationSpectrumActionType.ToggleChartFullscreen: {
            this.reducerToggleChartFullscreen();
            this.effectToggleChartFullscreen(action);
            break;
          }
        }
        return of(action);
      })
    )
  );

  private reducerGetData() {
    this.patchState({
      loading: true,
      error: null,
    });
  }

  private reducerGetDataSuccesses(action: FpCorrelationSpectrumActionGetDataSuccesses) {
    this.patchState({
      date: action.date,
      popupMainData: action.popupMainData,
      data: action.data,
      loading: false,
    });
  }

  private reducerGetDataError(action: FpCorrelationSpectrumActionGetDataError) {
    this.patchState({
      date: action.date,
      popupMainData: action.popupMainData,
      data: null,
      error: action.error,
      loading: false,
    });
  }

  private reducerToggleChartFullscreen() {
    const state = this.get();
    this.patchState({
      isChartFullscreenActive: !state.isChartFullscreenActive,
    });
  }

  private effectGetData(action: FpCorrelationSpectrumActionGetData) {
    const { correlation, correlationMode } = action.popupMainData || {};
    const mainProjectId = this.mainProjectId();

    if (!mainProjectId || !correlation) {
      this.dispatch({
        type: FpCorrelationSpectrumActionType.GetDataSuccesses,
        date: action.date,
        popupMainData: action.popupMainData,
        data: null,
      });

      return;
    }

    const state = this.get();

    const isOldDataValid =
      state.data &&
      state.popupMainData &&
      state.popupMainData.correlation.id === correlation.id &&
      state.popupMainData.correlationMode === correlationMode;

    if (isOldDataValid) {
      this.dispatch({
        type: FpCorrelationSpectrumActionType.GetDataSuccesses,
        date: action.date,
        popupMainData: action.popupMainData,
        data: state.data,
      });

      return;
    }

    this.correlationsChartDataApiService
      .getSpectrum(mainProjectId, correlation.id, correlationMode)
      .pipe(
        catchError(error => {
          this.dispatch({
            type: FpCorrelationSpectrumActionType.GetDataError,
            date: action.date,
            popupMainData: action.popupMainData,
            error,
          });

          return EMPTY;
        })
      )
      .subscribe(({ data }) => {
        this.dispatch({
          type: FpCorrelationSpectrumActionType.GetDataSuccesses,
          date: action.date,
          popupMainData: action.popupMainData,
          data: convertChartDataInArray<ICorrelationSpectrumChartDataPoint, ICorrelationSpectrumChartCommonData>(data),
        });
      });
  }

  private effectToggleChartFullscreen(action: FpCorrelationSpectrumActionToggleChartFullscreen) {
    if (!action.synchronizeWithPopupId) return;

    const state = this.get();

    if (state.isChartFullscreenActive) {
      this.popupService.enterFullscreen(action.synchronizeWithPopupId, false);
    } else {
      this.popupService.exitFullscreen(action.synchronizeWithPopupId, false);
    }
  }
}
